import React, { useEffect, useRef, useState } from 'react';
import { CFormInput } from '@coreui/react';

interface NumberInputProps {
    id?: string;
    value: number;
    onChange: (value: number) => void;
    size?: 'sm' | 'lg';
    style?: React.CSSProperties;
    step?: number;
    min?: number;
    max?: number;
}

/**
 * A controlled number input that lets the field go empty while typing — e.g. deleting a
 * lone "0" — instead of snapping back to "0" on every keystroke. A plain
 * `<CFormInput value={someNumber} .../>` can't tell "the field is empty" apart from "the
 * field holds 0," since both commit to the same parent value and re-render identically;
 * tracking the displayed text separately from the committed value (and skipping the sync
 * when the incoming value is just this component's own last emission echoing back) fixes
 * that without the parent needing to know its value can transiently be "no number yet."
 *
 * `min`/`max`, if given, are NOT enforced on every keystroke (that would make it impossible
 * to type a valid multi-digit number when an early digit briefly exceeds the bound, e.g.
 * typing "10" one digit at a time with max=10 would clamp to "1" partway through) --
 * they're only enforced on blur, once the user is done typing.
 */
export const NumberInput = ({
    id,
    value,
    onChange,
    size,
    style,
    step,
    min,
    max,
}: NumberInputProps): React.ReactElement => {
    const [text, setText] = useState(String(value));
    const lastEmitted = useRef<number | null>(null);

    useEffect(() => {
        if (value === lastEmitted.current) return;
        setText(String(value));
    }, [value]);

    return (
        <CFormInput
            id={id}
            size={size}
            type="number"
            step={step}
            min={min}
            max={max}
            value={text}
            style={style}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
                const raw = e.target.value;
                setText(raw);
                const parsed = parseFloat(raw);
                const next = Number.isNaN(parsed) ? 0 : parsed;
                lastEmitted.current = next;
                onChange(next);
            }}
            onBlur={() => {
                if (text === '') {
                    setText('0');
                    return;
                }
                const parsed = parseFloat(text);
                if (Number.isNaN(parsed)) return;
                let clamped = parsed;
                if (min !== undefined) clamped = Math.max(min, clamped);
                if (max !== undefined) clamped = Math.min(max, clamped);
                if (clamped !== parsed) {
                    setText(String(clamped));
                    lastEmitted.current = clamped;
                    onChange(clamped);
                }
            }}
        />
    );
};
